import cx from "classnames";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import removeMd from "remove-markdown";
import { useAppService } from "../../hooks/use-app-service";
import {
  getGroupPageRouteFromSlug,
  getSectionPageRouteFromSlug,
} from "../../_pages";
import { createEventDetailSlugPageRoute } from "../../_pages/event-detail.page";
import { createPostDetailPageRouteFromSlug } from "../../_pages/post-detail.page";
import { createUserWallPageRoute } from "../../_pages/user-wall.page";
import { getSearchResults } from "../../_store/search.store";
import { CloseButton } from "../activity/close-button.component";
import { Avatar } from "../avatar.component";
import IconButton2 from "../icon-button-2-component";
import { Loader } from "../loader.component";
import ModalPortal from "../Portal";
import Label from "../typography/label.typography";
import UIcon from "../uicon-component";

function SearchCommunityIconComponent({ user, search, getSearchResults }) {
  const { isLoading, results } = search;
  const { analyticsService } = useAppService();

  const hasData = results && Object.values(results).some((r) => r.length);

  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [searchedQuery, setSearchedQueary] = useState();

  const triggerSearch = useCallback(debounce(getSearchResults, 1000), []);

  //mix panel event
  useEffect(() => {
    if (hasData) {
      analyticsService.track("search", {
        searchedContent: searchedQuery,
      });
    }
  }, [hasData, searchedQuery, analyticsService]);

  // TODO: handle enter in search bar
  const handleEnterInSearchBar = useCallback(
    async (event) => {
      if (event.key === "Enter") {
        console.log("enter");
        await getSearchResults();
      }
    },
    [getSearchResults]
  );

  const inputRef = React.useRef();

  useEffect(() => {
    if (!isSearchVisible) {
      getSearchResults();
    }
  }, [isSearchVisible]);

  return (
    <div className="pl-6">
      <IconButton2
        className="w-5 sm:w-10 mt-1 mr-1"
        icon="search"
        onClick={() => {
          setIsSearchVisible(!isSearchVisible);
        }}
      />

      <ModalPortal openPortal={isSearchVisible}>
        <div
          className="fixed bg-gray-700/50 top-0 bottom-0 left-0 right-0 pt-24 "
          style={{
            zIndex: 120,
          }}
          onClick={(e) => {
            setIsSearchVisible(!isSearchVisible);
          }}>
          <div
            className="mx-auto max-w-2xl rounded-lg theme-bg-surface z-20"
            onClick={(e) => {
              e.stopPropagation();
            }}>
            {/* header */}
            <div
              className={cx(
                "flex items-center justify-between py-1 border theme-border-default ",
                {
                  "rounded-lg": !hasData,
                  "rounded-t-lg": hasData,
                }
              )}>
              <div className="flex-1 flex items-center theme-bg-default ml-1 rounded-lg">
                <div className="mx-1 flex w-6 h-6 items-center place-content-center">
                  {isLoading ? (
                    <Loader size={20} />
                  ) : (
                    <UIcon icon="search" size="lg" />
                  )}
                </div>

                <input
                  className="text-base w-full p-2 theme-bg-default focus:outline-none rounded-lg"
                  placeholder="Search community"
                  onChange={(e) => {
                    setSearchedQueary(e.target.value);
                    triggerSearch(e.target.value);
                  }}
                />
              </div>

              <CloseButton
                onClick={() => {
                  setIsSearchVisible(!isSearchVisible);
                }}
                size="md"
                type="primary"
                className="mx-1"
              />
            </div>
            {/* input */}
            {hasData ? (
              <Results results={results} />
            ) : (
              // <NoResultFound query={inputRef?.current?.value} />
              <></>
            )}
          </div>
        </div>
      </ModalPortal>
    </div>
  );
}

function NoResultFound({ query }) {
  return (
    <div className="flex flex-col items-center justify-center h-96 py-44">
      <div className="flex items-center justify-center gap-2">
        <UIcon icon="search" size="xl" className="theme-text-subtitle-1" />
      </div>
      <div className="text-center">
        <Label variant="s1">No results found</Label>
        <Label variant="s1" className="font-bold">
          {query}
        </Label>
      </div>
    </div>
  );
}

function Results({ results }) {
  const { comments, events, groups, posts, replies, sections, users } = results;

  const total =
    comments.length +
    events.length +
    groups.length +
    posts.length +
    replies.length +
    sections.length +
    users.length;

  return (
    <div className="border-x border-b rounded-b-lg theme-border-default">
      <div className="max-h-[360px] overflow-auto">
        <ResultList source={groups} sourceName="group" titleKey="name" />
        <ResultList source={events} sourceName="event" titleKey="title" />
        <ResultList source={posts} sourceName="post" titleKey="title" />
        <ResultList
          source={comments}
          sourceName="comment"
          titleKey="description"
          noDesc
        />
        <ResultList
          source={replies}
          sourceName="reply"
          titleKey="description"
          noDesc
        />
        <ResultList
          source={sections}
          sourceName="section"
          titleKey="name"
          noDesc
        />
        <ResultList source={users} sourceName="user" titleKey="name" noDesc />
      </div>
      <div className="p-4 theme-text-subtitle-1">
        {total} <Label variant="s1">Result{total === 1 ? "" : "s"}</Label>
      </div>
    </div>
  );
}

function ResultList({
  source = [],
  titleKey = "name",
  sourceName,
  noDesc = false,
}) {
  const { analyticsService } = useAppService();

  return source.map((s) => {
    let title = removeMd(s[titleKey] ? s[titleKey] : s.description);
    let description = removeMd(s.description);

    if (!s[titleKey]) {
      noDesc = true;
    }

    if (title.length > 28) {
      title = title.slice(0, 25) + "...";
    }

    if (!noDesc && description.length > 88) {
      description = description.slice(0, 85) + "...";
    }
    return (
      <div
        className="cursor-pointer hover:theme-bg-default px-4 py-3 flex items-center border-b theme-border-default"
        key={s.id}
        onClick={(e) => {
          const link = generateEntityRoute(s, sourceName);
          if (link) {
            window.open(link, "_blank");
            analyticsService.track("searched-result-clicked", {
              searchedType: sourceName,
              searchedUrl: window.location.origin + link,
            });
          }
        }}>
        <div className="flex flex-col details flex-grow">
          <span className="flex items-center">
            {sourceName === "user" && s?.picture && (
              <Avatar user={s} noName size={25} />
            )}
            <Label size="body">{title}</Label>
          </span>

          {!noDesc && <Label variant="s2">{description}</Label>}
        </div>
        <div>
          {sourceName ? (
            <div className="theme-border-default border rounded px-2 py-0.5 capitalize">
              <Label variant="s1" size="small" translate>
                {sourceName}
              </Label>
            </div>
          ) : (
            ""
          )}
        </div>
      </div>
    );
  });
}

function generateEntityRoute(source, type) {
  switch (type) {
    case "group":
      return getGroupPageRouteFromSlug(source);
    case "section":
      return getSectionPageRouteFromSlug(source.group, source);
    case "post":
      return createPostDetailPageRouteFromSlug(source);
    case "comment":
    case "reply":
      return createPostDetailPageRouteFromSlug(source.post);
    case "user":
      return createUserWallPageRoute(source.id);
    case "event":
      return createEventDetailSlugPageRoute(source);
    default:
      return null;
  }
}

const SearchCommunityIcon = connect(
  (s) => ({
    user: s.auth,
    search: s.search,
  }),
  {
    getSearchResults,
  }
)(SearchCommunityIconComponent);

export default SearchCommunityIcon;
